import axios from 'axios';
import { isStopped } from './stoppableCalls';
import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import type { PDFDocumentLoadingTask, PDFDocumentProxy } from 'pdfjs-dist';
import { loadPdf, renderPdfPageBand } from './pdfRender';
import { buildStepAnalyzePrompt, GAP } from './stepAnalyzePrompt';
import { loadExpertRules } from './expertRulesStore';
import { catalogueSubset, partKindWord, partSummaryLabel, shownId, storedId }
  from './partsCatalogue';
import { asSubStepKind, MADE_ID_BASE, makesHardware, type SubStepKind } from './subStepKinds';
import { categoriesLoaded } from './catalogCategories';
import type { StepBand } from './categoryBands';
import { symmetryOf, type PartItem, type Side } from './partsRun';
import { getStepTools as partsStepTools, type ByStepParts, type ByStepText, type StepPart }
  from './stepPartsRun';
import { haltGate } from './runHalt';
import { digest } from './digest';
import { refileStep } from './refile';
import { storedTokens } from './subStepText';
import { BEAT_MS, STEPS_PHASE, setWorking, watchRemoteStop } from './phaseWorking';

// Per-assembly-step analysis for a document: in ONE Gemini call per step we get its
// tools, what it builds on, its written instructions and its breakdown into sub-steps.
// Like the other runs it lives at module scope (keyed by document id) so it survives
// leaving and returning to the reader tab, and is persisted server-side. One latest
// result per doc (no history): re-running replaces it.
//
// Which parts the step ADDS is not asked here — Phase 2 shares the supplied quantities
// out among the steps in one pass (see stepPartsRun) and this call is told the answer.

// A part and how many of it, on a SUB-STEP: what ONE pass consumes — unless `once` is set,
// which means the whole quantity is consumed on the first pass alone (the piece the
// repeated units are fastened to: one frame, brought once, already there on the second
// pass).
//
// `once` IS ASKED FOR AGAIN, and it is what makes a cycle's arithmetic possible: four dowels
// go into ONE side rail, so the rail is taken once for the whole run and the dowels once per
// pass. It spent a while unasked and still read — the server dropped it, nothing wrote it,
// and Reconciliation went on honouring it — which is how a part shared by a cycle's passes
// could only be made to add up by calling it unconsumed.
// `consumed: false` marks a part whose units are counted somewhere ELSE: every part of a
// placing (a placing consumes nothing), and a piece an earlier sub-step assembled, named by
// the ids it was made from. It counts toward no total, so it may be listed by several
// sub-steps while still being used up by exactly one. Absent means consumed, which is what
// every entry written before the flag means. It no longer turns on which piece MOVED — that
// clause predated the kinds and contradicted them.
// `role` is which END of the fitting this part is: the one being added — brought and put onto
// something, the part that moves — or the one receiving it, which stays where it is. Phase 4
// answers it because Phase 4 wrote the sentence that says which way round the gesture goes;
// Phase 5 was asking a second reader the same question about the same sentence.
//
// Absent on every sub-step analysed before it was asked for, which is "not answered" and not a
// third role: nothing may default a part to moving.
// `side` is WHICH OF A MIRROR PAIR this gesture holds, where the part is one — see
// PartItem.symmetry, which is where a part is established as having two hands at all. On the
// entry and not on the part, because the part is both: "side rail (C)" names a left one and a
// right one, and which of them a sub-step fastens is a fact about that sub-step. Absent
// everywhere else, which is nearly every entry — a screw has no handedness to state — and
// absent on every sub-step analysed before it was asked for, which is not a third side.
// `prepared`: hardware put onto the added part in the hand, before it goes in — a washer onto a
// screw (see the prompt). Played first by the gesture that names it (see Slot.prepares).
export type PartRole = 'added' | 'receiving' | 'prepared';
export type UsedPart = StepPart & {
  consumed?: boolean;
  role?: PartRole;
  side?: Side;
};

// HOW MANY UNITS ONE ROW ACCOUNTS FOR — the quantity it was given, and nothing else. It used
// to multiply by the passes of the cycle and exempt the parts marked `once`, which is the
// arithmetic that went with the cycles.
export const usedCount = (p: UsedPart) => p.qty ?? 1;

// One language's transcribed instruction text for a step.
export type InstrEntry = { lang: string | null; text: string };
// The name of the piece a combining sub-step leaves behind, in the app's two languages —
// the same shape as everything else here that a reader sees in their own.
export type SubStepOutput = { en: string; fr: string };

// THE OUTPUT'S NAME, in the language on screen and starting with a capital.
//
// CAPITALISED HERE, not by the model. The green chip beside it reads "Barrel nut (G)" because
// that is the catalog's own name for a thing; this one is a phrase the model wrote to sit mid
// sentence, and it comes back "assembled frame". Two chips on one row, one capitalised and one
// not, is a difference a reader stops to explain to themselves — and the prompt is the wrong
// place to fix it, since what it asks for is a noun to write into a sentence rather than a
// label. The FIRST character only: anything more would flatten a name the model got right.
export function subStepOutputName(
  output: SubStepOutput | null | undefined,
  language: string,
): string {
  const raw = (language === 'fr' ? output?.fr || output?.en : output?.en || output?.fr) || '';
  return raw ? raw[0].toUpperCase() + raw.slice(1) : '';
}
// One ordered sub-step of an assembly step: its letter, a bilingual one-line
// description, and the parts/tools it consumes. Those are the MODEL'S answer, stored as
// given: the server used to rewrite the quantities so they partitioned the step totals
// exactly, and no longer does. Phase 4's reconciliation checks that sum and says when it
// disagrees, which is the same fact reported rather than erased.

export { subStepLabel } from './subStepName';

export type SubStep = {
  // ITS NUMBER IN ITS STEP, from 1, as a string — "2". Named `letter` from when it was "ab".
  letter: string;
  // WHICH OF THE FOUR KINDS OF GESTURE this is, 1 to 4 — see subStepKinds, which defines
  // them and is what the prompt asking for this field is built from. Null where nobody
  // answered: a sub-step analysed before the kinds were asked for, or a reply outside the
  // list. Never defaulted to a number, because "unclassified" and "classified as a 1" are
  // different facts and only one of them is worth re-running a step over.
  kind: SubStepKind | null;
  textEn: string;
  textFr: string;
  // THE SAME SENTENCE CUT INTO THE MOMENTS PLAYED IN TURN — the preparation alone, then the rest —
  // shown one at a time under Phase 6's animation and the published clip (see captionAt). Absent
  // on a sub-step analysed before they were asked for, which shows its sentence whole.
  beatsEn?: string[];
  beatsFr?: string[];
  // WHAT THE GESTURE LEAVES: the piece the two or three became, named in both languages.
  // "Prepared screw", "vis préparée" — a noun, because a later sub-step picks it up and the
  // thing it picks up has to be sayable.
  //
  // ONLY A COMBINING HAS ONE. A placing fastens nothing, so there is no new piece to name,
  // and the field is null for kinds 1 and 2 by definition rather than by omission. Null also
  // on every sub-step analysed before it was asked for — which is why the column that draws
  // it tells a placing (nothing to show) apart from a combining with no answer (a dash).
  output: SubStepOutput | null;
  // THERE ARE NO CYCLES ANY MORE. A sub-step carried `cycle` and `times`, and a run of them
  // said "these three gestures, four times" — one row standing for four. It was a compression
  // of the manual, and every number within reach of it then had to declare which frame it was
  // in: a quantity was per PASS, `times` was the run, `once` marked a part that escaped the
  // per-pass rule, and what a repeated gesture LEFT was one piece per pass except where the
  // passes shared one.
  //
  // That seam is where the errors were, not at its edges. A gesture claiming two of a piece
  // its own cycle makes one of per pass; a chair frame reading ×4 because four passes were
  // counted as four frames; a ring in the tree drawn round a subtree because a run is not a
  // subtree. Each was the same fault: two readers of one number disagreeing about what it
  // counted.
  //
  // A gesture is one gesture now. Four rails is four sub-steps, and every quantity means the
  // same thing wherever it is read.
  parts: UsedPart[];
  tools: string[];
  // THE SUB-STEP AS A MOVEMENT: which part is placed, what it is placed onto, and with
  // which tool. Ids into the Phase 2 parts list, like everything else here.
  //
  // The sentence beside it already says all three; this is the same answer as data, because
  // a reader can act on prose and a viewer cannot. Phase 5 fills its three selectors from
  // it. Null for a sub-step that fits nothing — turning the assembly over — and absent on
  // steps analysed before it was asked for, which is not the same as null and is why the
  // phase only ever uses it to PREFILL an unanswered slot.
  fitting?: SubStepFitting | null;
};

// NO KIND. It used to carry one — 'fitting' where one part is joined to another, 'placing'
// where a single part is moved and joined to nothing — and that pair is now the two FAMILIES
// of SubStep.kind, which the sub-step answers as one of four and this had no business
// answering a second time. Two fields for one distinction is two fields that disagree: a
// sub-step could come back a combining, driving a fastener home, beside a movement
// calling itself a placing.
//
// So placing-vs-combining is read off `SubStep.kind` (see subStepFamily), and where that is
// null — every manual until its steps are re-run — off the ids below, which state the same
// distinction the longer way: something to be fitted ONTO is a combining. See assemblySlots,
// which is the one place that decides it.
//
// NO TOOL either, and for its own reason. The model was asked which tool makes the movement,
// and that was a field with one possible right answer already on file: no sub-step uses two
// tools at once, so the tool Phase 4 lists for it IS the tool, and a second opinion could
// only differ from it by being wrong. The phase takes it from the sub-step now (see
// Slot.tool). Answers stored back when either was asked still have the field on disk;
// nothing reads them.
export type SubStepFitting = {
  moves: string | null;
  onto: string | null;
};

// The clip length every sub-step starts at, overridden per sub-step by the composer's
// dropdown. A constant rather than a per-sub-step estimate: Phase 4 used to judge how long
// each gesture takes, which was a reasonable question to ask a model that had read the
// diagram, but it made the length of a clip something inferred rather than chosen — and the
// only way to learn what length actually works for these clips is to try lengths and watch.
// The dropdown is where that happens now.

// step category ("Assemblage - Etape N") -> that step's instructions.
type ByInstr = Record<string, InstrEntry[]>;
// step category -> the numbers of the earlier steps whose output this step builds
// upon (its "from previous steps"). Each references a producing step, not a part.
type ByBuildsOn = Record<string, number[]>;
// step category -> ids of the tools this step uses (not consumed).
type ByTools = Record<string, string[]>;
// step category -> its ordered sub-steps.
type BySubSteps = Record<string, SubStep[]>;

type Snapshot = {
  busy: boolean;
  // THE STEP WHOSE CALL IS IN FLIGHT, by category. `busy` alone says the phase is working
  // and cannot say WHERE — the run walks a dozen steps one at a time, so "this step is being
  // read right now" and "this step is waiting its turn" are different facts, and the table
  // draws them differently. A list, though one step is in flight at a time: it was written
  // for a pool of four, and it is the shape the table already reads. Never persisted: a run
  // does not survive a reload.
  running: string[];
  tools: ByTools;
  buildsOn: ByBuildsOn;
  instructions: ByInstr;
  subSteps: BySubSteps;
  // The model's reasoning per step (shown in that step's tooltip), and how many seconds its
  // call took — the same tooltip's other half.
  reasoning: Record<string, string>;
  timing: Record<string, number>;
  error: string | null;
  // Steps done / total while extracting (null when idle).
  progress: { done: number; total: number } | null;
  // WHEN THE RUN STARTED (ms), and how long the last finished one took (seconds). Held here
  // rather than counted by the button, because the run outlives the button: the phase now
  // walks its steps one at a time, and a screen mounted halfway through a run must be able
  // to say how long it has ACTUALLY been going rather than how long it has been watching.
  //
  // `elapsed` is what the button keeps showing once the run ends — the time the last run
  // took, standing where its progress count was. Neither is persisted, for the reason
  // `progress` and `running` are not: a run does not survive a reload, so neither does a
  // stopwatch reading of one.
  startedAt: number | null;
  elapsed: number | null;
};
const loadSubSteps = (docId: string): BySubSteps => {
  const stored = readState<Record<string, Partial<SubStep>[]>>(docId, subStepsKey(docId), {});
  const out: BySubSteps = {};
  for (const [category, subs] of Object.entries(stored)) {
    out[category] = (subs || []).map((s) => ({
      letter: s?.letter ?? '',
      // Through the same guard the server uses, so a stored 7 — or a stored "3" — reads as
      // unanswered here rather than as a kind no table has a name for.
      kind: asSubStepKind(s?.kind),
      textEn: s?.textEn ?? '',
      ...(s?.beatsEn?.length ? { beatsEn: s.beatsEn } : {}),
      ...(s?.beatsFr?.length ? { beatsFr: s.beatsFr } : {}),
      textFr: s?.textFr ?? '',
      // Null and not {en:'', fr:''}: an empty pair would draw an empty chip, and "this
      // gesture leaves a piece nobody named" is not what a sub-step from before the question
      // means. Carried here the day the field was written — see the note below this list.
      output: s?.output?.en || s?.output?.fr
        ? { en: s.output.en ?? '', fr: s.output.fr ?? '' }
        : null,
      parts: s?.parts ?? [],
      tools: s?.tools ?? [],
      // EVERY FIELD OF A SUB-STEP IS LISTED HERE, and a new one is listed the day it is
      // written. A field left out of this mapper lives in memory until the first reload and
      // then quietly is not there any more — which reads as a manual that was never asked
      // rather than as a bug, since "no entry" and "no answer" are deliberately different
      // states downstream.
      //
      // And passed through AS IT IS: undefined and null mean different things to Phase 5 —
      // never asked against asked and answered "this sub-step fits nothing" — so neither is
      // defaulted to the other.
      fitting: s?.fitting ?? null,
    }));
  }
  return out;
};

const store = createDocEntryStore<Snapshot>((docId) => ({
  snapshot: {
    busy: false,
    running: [],
    tools: readState<ByTools>(docId, toolsKey(docId), {}),
    buildsOn: readState<ByBuildsOn>(docId, buildsOnKey(docId), {}),
    instructions: readState<ByInstr>(docId, instrKey(docId), {}),
    subSteps: loadSubSteps(docId),
    reasoning: readState<Record<string, string>>(docId, reasonKey(docId), {}),
    timing: readState<Record<string, number>>(docId, timingKey(docId), {}),
    error: null,
    progress: null,
    startedAt: null,
    elapsed: null,
  },
}));

const toolsKey = (docId: string) => `notice.steps.tools.${docId}`;
const buildsOnKey = (docId: string) => `notice.steps.buildsOn.${docId}`;
const instrKey = (docId: string) => `notice.steps.instr.${docId}`;
const subStepsKey = (docId: string) => `notice.steps.subSteps.${docId}`;
const reasonKey = (docId: string) => `notice.steps.reason.${docId}`;
const timingKey = (docId: string) => `notice.steps.timing.${docId}`;


// Merge a partial into this document's snapshot and re-render — the shared helper.
const update = store.patch;

// One step enters or leaves the in-flight list. Read-modify-write against the store rather
// than against a local set: the snapshot is the only copy of it anything sees.
const markRunning = (docId: string, category: string, on: boolean) => {
  const cur = store.get(docId).running;
  update(docId, {
    running: on ? [...cur, category] : cur.filter((c) => c !== category),
  });
};

// The five persisted maps of a step run. Written together in two places (blank on a
// fresh run, and after each analyzed step); one helper so the five keys can never drift
// apart.
type StepMaps = {
  tools: ByTools;
  buildsOn: ByBuildsOn;
  instructions: ByInstr;
  subSteps: BySubSteps;
  reasoning: Record<string, string>;
  // HOW LONG THE CALL TOOK, in seconds, per step. Kept beside the reasoning because it is the
  // same thing: what happened when this step was asked, as against what the answer says. A
  // phase whose steps take twenty seconds and one whose steps take four minutes are different
  // phases, and until this was stored the difference was only ever in a server log.
  timing: Record<string, number>;
};
function persistStep(docId: string, m: StepMaps) {
  writeState(docId, toolsKey(docId), m.tools);
  writeState(docId, buildsOnKey(docId), m.buildsOn);
  writeState(docId, instrKey(docId), m.instructions);
  writeState(docId, subStepsKey(docId), m.subSteps);
  writeState(docId, reasonKey(docId), m.reasoning);
  writeState(docId, timingKey(docId), m.timing);
}

// ---- The parts context sent to the model, built ONCE here so the on-screen "context
// sent to Gemini" preview (VideoPromptComposer) is byte-for-byte what step-analyze
// embeds. It has four parts, mirroring the prompt: the full supplied-parts catalogue
// (grouped by ontology category, named with the database/catalog name, the letter kept),
// then the parts THIS step adds — Phase 2's repartition, stated as given — then what
// earlier steps have already assembled, then what each earlier step produced. ----

// The parts/context block sent with a step (and previewed for it). Built here so the
// preview shows exactly what is sent.
export function buildStepPromptContext(
  items: PartItem[],
  // `tools` is Phase 3's answer FOR THIS STEP — see getStepTools. Not the manual's tools:
  // see the tools section below for why the difference is the whole point.
  step: { parts: StepPart[]; text: string; tools: string[] },
  assembled: { id: string; qty: number | null }[],
  previousSteps: { step: number; parts: StepPart[]; text: string }[],
  // The pieces earlier steps' sub-steps assembled, with the ids they were given — see
  // piecesBefore. A step may pick one up, and cannot name it without being told it exists.
  pieces: { id: string; name: string; base?: string; inside: string[] }[] = [],
): { adds: string; context: string } {
  const stepParts = step.parts;
  // WITH ITS FRENCH NAME where it has one: textFr puts an article before each token, and the
  // article is the noun's gender — "la {5}" for a vis — which an English name cannot tell.
  const label = (id: string) => {
    const p = items[Number(id)];
    const fr = p?.nameFr?.trim();
    const said = partSummaryLabel(p, id);
    return fr && !said.startsWith(fr) ? `${said} [fr: ${fr}]` : said;
  };
  const qtyStr = (q: number | null | undefined) => `x${q ?? '?'}`;
  // COMPONENT OR HARDWARE, on every line that names a part. The four kinds of sub-step are
  // defined by how many of each a gesture takes — "1 component part, 1 hardware part, 0 or 1
  // tool" — and that rule is undecidable in front of a list reading "4: JCBC screw (E) x4".
  // A spring washer is hardware because the ontology says its category is; nothing here has
  // to know the word "washer".
  //
  // NOTHING AT ALL until the ontology has been fetched. Unloaded, every category resolves to
  // the default and every screw in the manual would be labelled a component part — which is
  // not a vaguer answer than the truth, it is the opposite of it, and it is the input to the
  // rule that decides whether a gesture is a kind 2, 3 or 4. Unlabelled, the model still has
  // the names: "spring washer (F)" is legible where "[component]" is a lie.
  const kindStr = (id: string) => {
    const p = items[Number(id)];
    return p && categoriesLoaded() ? ` [${partKindWord(p)}]` : '';
  };
  // "3: leg (letter D) x4 (2 per side rail, 2 side rails)" — the id first, because the id
  // is what the sub-steps answer in, and the derivation after the total, because the
  // sub-steps are written for ONE of those units.
  // NO DERIVATION AFTER THE TOTAL. It read "4: JCBC screw (E) x4 (2 per side rail, 2 side
  // rails)", which is Phase 2's working — how it shared the manual's supply out — and this
  // call has no use for it: the sub-steps below decide how the four are spent, and a
  // parenthesis proposing a split of two-by-two is an answer offered to a question the model
  // is about to be asked. The total is the fact; the arithmetic behind it is not.
  // AND WHICH PARTS COME IN HANDS. A step that adds two side rails adds a left one and a
  // right one, and nothing in this block ever said so: the model was shown "2: side rail (C)
  // x2" and wrote two gestures that were the same gesture twice. It is stated on the line
  // rather than in a rule at the top for the reason the kind is: the rule is applied while
  // reading the list, and a list that carries its own exceptions needs no cross-reference.
  //
  // Only for a part that IS one. 'identical' is the ordinary answer and saying it out loud on
  // every screw would bury the two lines that are news.
  const symStr = (id: string) =>
    (symmetryOf(items[Number(id)]) === 'left-right' ? ' [a LEFT/RIGHT mirror pair]' : '');
  const line = (u: StepPart) =>
    `${shownId(u.id)}: ${label(u.id)}${kindStr(u.id)}${symStr(u.id)} ${qtyStr(u.qty)}`;

  // THE TOOLS THIS STEP CALLS FOR, and not the manual's. It listed every tool in the parts
  // list — a hex key, a screwdriver, a spanner, a mallet — of which a given step uses one, and
  // the other three are exactly the wrong kind of reference: this call answers "tools" per
  // sub-step, so a tool it can see is a tool it can name, and nothing in the prompt said the
  // step does not use it. Which tool each step calls for is already ANSWERED, by Phase 3, from
  // the reader that had every step's diagram at once (see getStepTools) — it was being asked
  // for a second time here, of a reader with one step's diagrams, by offering it the list.
  //
  // Filtered to ids that really are tools: the stored answer is a model's, and a step that
  // named a screw among its tools would otherwise put that screw under a heading saying it is
  // never consumed — beside an adds block giving the total it must be consumed to.
  const toolIds = [...new Set(step.tools.map(Number))].filter((i) => items[i]?.tool);
  const toolsSection = toolIds.length
    ? 'Tools THIS step calls for. Never consumed and never counted — use these ids in a ' +
      'sub-step\'s "tools":\n' +
      catalogueSubset(items, toolIds).replace(/^/gm, '  ')
    : '';

  // WHAT IS ALREADY THERE still travels as context, below: a sub-step must list the piece the
  // new part is fastened TO with "consumed": false, and that piece came from an earlier step,
  // so it is in neither of the two lists above.
  //
  // The whole catalogue is not sent at all any more — every part outside these lists was
  // reference for a question nobody asked here, and worse than idle: the CRITICAL rule pins
  // quantities only for parts with a total in the adds block, so a sixth screw the catalogue
  // named and this step never touches had nothing to contradict it.

  // What the step achieves, from the reader who saw every diagram in sequence. It leads the
  // adds block rather than trailing the catalogue: it is the sentence that says what the
  // parts below are FOR, and it was sitting after fourteen catalogue rows, where it read as
  // a footnote to the supplied list instead of as the heading of this step.
  const doesSection = step.text ? `What THIS step achieves: ${step.text}${GAP}` : '';

  // ONE PART PER LINE. They were a comma-run — "0: A x1, 2: C x2, 4: E x4 (2 per side rail,
  // 2 side rails), …" — in which the commas inside a part's own derivation are the same
  // character as the commas between parts, so the list has no visible joints. Six parts read
  // as one sentence, and the totals that every sub-step below has to add up to are the one
  // thing here that must be countable at a glance.
  // WHAT THE STEP ADDS, RETURNED ON ITS OWN so the prompt can put it where it belongs: in
  // the opening, under the sentence that says the sub-steps must account for exactly these
  // units and no others. It sat with the rest of the context, four screens down, so the rule
  // was stated at the top and the numbers it is about were somewhere near the bottom — and
  // this list is the one thing in the whole prompt every sub-step has to be checked against.
  const addsStr = stepParts.map((u) => `  ${line(u)}`).join('\n');
  // THE TOOLS DIRECTLY UNDER THE PARTS, in the same block and by the same argument the adds
  // block itself was moved up for: they are the other half of what this step has in hand, and
  // one sentence of the prompt asks for both — "the parts this sub-step handles" and "the
  // tools this sub-step uses". Sent as context, four paragraphs down among the earlier steps,
  // the two lists a sub-step answers from were at opposite ends of the prompt.
  const addsSection =
    doesSection +
    'What this step consumes:\n' +
    (addsStr || '  (no new supplied parts — this step only works on what is already there)') +
    (toolsSection ? GAP + toolsSection : '');

  // NOTHING WHERE THERE IS NOTHING. A step with no assembly behind it used to say so here,
  // at the far end of the prompt under a heading about earlier steps — which reads as an
  // empty list rather than as the reason there is none. The sentence is in the opening now
  // (see buildStepAnalyzePrompt), and this section is simply absent.
  //
  // WITH THEIR IDS, one per line. It was a comma-run of names — "Front legs frame (B) x1,
  // Seat (D) x1" — and a sub-step that merely handles one of these has to answer for it by
  // id, which was the one thing the line did not give. That id used to be findable in the
  // full catalogue below it; there is no catalogue below it any more.
  const assembledStr = assembled
    .map((a) => `- ${shownId(a.id)}: ${label(a.id)}${kindStr(a.id)} ${qtyStr(a.qty)}`)
    .join('\n');
  const assembledSection = assembledStr
    ? 'Already assembled from earlier steps (cumulative). These are redrawn in this ' +
      'diagram for context and are NOT what this step adds — use these ids for a piece the ' +
      'action only handles:\n' +
      assembledStr
    : '';

  // Each earlier step with what it achieved as well as what it consumed. "buildsOn" asks
  // which earlier sub-assemblies this diagram shows, and a list of parts is a poor way to
  // recognise one: "the front leg frame" is recognisable, "B x1, C x2" is a puzzle.
  const previousStr = previousSteps
    .map((s) => {
      const bits = s.parts.map((u) => `${label(u.id)} ${qtyStr(u.qty)}`).join(', ');
      const what = s.text ? `${s.text} — ` : '';
      return `Step ${s.step}: ${what}${bits || '(no new supplied parts)'}`;
    })
    .join('\n');
  const previousSection = previousStr
    ? 'Earlier steps, what each achieved and what each consumed (reference these ' +
      'numbers in "buildsOn"):\n' +
      previousStr
    : '';

  // WHAT EARLIER STEPS BUILT, by the ids they hold. The "already assembled" list above is
  // Phase 2's arithmetic — which SUPPLIED units have gone in — and it cannot name the thing
  // they became: a frame with two rails in it is four rows of that list and no noun. These are
  // the nouns, and the ids a sub-step needs to say it picks one up. Listed whole: which of them
  // are still loose is a fact about the gestures, and this call is reading the gestures.
  const piecesSection = pieces.length
    ? 'Pieces earlier steps assembled. Use these ids for a piece this step picks up, and do ' +
      'not reuse their names for a piece this step leaves:\n' +
      // WHAT IS INSIDE EACH, so a sentence can name the one part a gesture happens on rather
      // than the piece around it (see the prompt's "NAME THE PART WHERE THE GESTURE HAPPENS").
      pieces.map((p) => `  ${p.id}: ${p.name}${
        p.inside.length ? ` — made of ${p.inside.map(shownId).join(', ')}` : ''}${
        p.base ? ` — drawn in a sentence as ${label(p.base)}` : ''}`).join('\n')
    : '';

  return {
    adds: addsSection,
    context: [assembledSection, piecesSection, previousSection].filter(Boolean).join(GAP),
  };
}

// What the step at `idx` is given: the parts it adds, the cumulative assembly before it,
// and what each earlier step produced — all read off Phase 2's repartition. Tools are
// reused, never consumed, so they never enter the assembly.
//
// Every earlier step counts, with no "has it been analysed yet" gate: the repartition is
// one answer about the whole manual, so a step's context is complete from the first call,
// whether the steps before it have been analysed, analysed in another order, or not at
// all. It used to be folded from the per-step answers as they arrived, which meant the
// context a step got depended on the order the user had clicked in.
export function stepAssemblyInputs(
  steps: StepBand[],
  byStep: ByStepParts,
  text: ByStepText,
  items: PartItem[],
  idx: number,
): {
  assembled: { id: string; qty: number | null }[];
  previousSteps: { step: number; parts: StepPart[]; text: string }[];
} {
  const totals = new Map<string, number>();
  const previousSteps: { step: number; parts: StepPart[]; text: string }[] = [];
  for (let i = 0; i < idx && i < steps.length; i++) {
    const step = steps[i];
    const stepParts = (byStep[step.category] || []).filter((u) => !items[Number(u.id)]?.tool);
    for (const u of stepParts) totals.set(u.id, (totals.get(u.id) ?? 0) + (u.qty ?? 0));
    // English throughout: this is prompt text, and the prompt is English. The French half
    // exists for the reader, not for the model.
    previousSteps.push({ step: step.num, parts: stepParts, text: text[step.category]?.en ?? '' });
  }
  const assembled = [...totals.entries()].map(([id, qty]) => ({ id, qty }));
  return { assembled, previousSteps };
}

// WHERE THE MADE PIECES' NUMBERING STANDS in front of step `idx`, and what those pieces are.
//
// A piece assembled during a step gets an id of its own, counting from 101 (see MADE_ID_BASE
// in SubStepChips, which is where the display side of the same numbering lives). It restarted
// at 101 in every step for a day, which made "101" mean four different things in a four-step
// manual — and a step that picks up what an earlier one built could then only point at it
// ambiguously.
//
// ONE SEQUENCE THROUGH THE WHOLE MANUAL, then, for the same reason the cycles are one sequence:
// a number that identifies a thing has to identify one thing. The steps are asked in printed
// order precisely so this count exists.
//
// A PIECE IS A COMBINING WITH A NAME. That is the same test the table applies (see madePieces),
// and the two must agree exactly: the model is told its first piece is 105 and the table
// numbers the fifth piece it draws 105, so a different test on either side would silently
// rename everything downstream of the disagreement.
// EVERY PIECE THE MANUAL ASSEMBLES, by the sub-step that leaves it — `${category}#${letter}`,
// the key every phase addresses a sub-step by. THE numbering, in one place: the prompt is told
// where it stands, Phase 4's table draws the ids beside the chips, and Phase 5 prints the piece
// a gesture leaves. Three readers, and if any of them counted for itself they would agree until
// the day the test for "is this a piece" changed in one of them.
// NUMBERED BY THE STEP THAT MAKES THEM: step 1's pieces are 101, 102, 103; step 2's are 201,
// 202; step 12's are 1201. It ran as one sequence through the manual, 101 up, which is a
// number that says nothing — 108 was a piece somewhere in the middle, and finding out where
// meant reading down the table until it appeared. Read off the id, a piece now says which
// step it belongs to before it says anything else.
//
// The hundreds are also what makes an id a PIECE rather than a supplied part: those are
// indices into the parts list, and a manual does not run to a hundred parts. See
// MADE_ID_BASE, which is the same threshold and stays true of every step's range.
export const stepPieceBase = (stepNo: number) => stepNo * 100 + 1;

// A PIECE WHERE A CATALOG ITEM IS ASKED FOR — the assembly phase's two ends of a fitting are catalog
// ids, and a piece assembled by an earlier sub-step is in no catalog. Its own id, negative: no row
// of the Parts DB is, so the two never meet. Null for a supplied part.
export const pieceItemId = (id: string | null | undefined): number | null =>
  (id != null && Number(id) >= MADE_ID_BASE ? -Number(id) : null);

export function pieceIdByKey(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
): Map<string, { id: string; output: SubStepOutput }> {
  const out = new Map<string, { id: string; output: SubStepOutput }>();
  for (const step of steps) {
    let n = stepPieceBase(step.num);
    for (const sub of subSteps[step.category] ?? []) {
      // A PIECE IS A GESTURE WITH A NAME, whatever its kind. A sub-step nobody named leaves a
      // thing that cannot be referred to — every manual analysed before the field was asked
      // for is in that state, and giving those an id would number things no later sub-step
      // can name.
      //
      // IT TOOK A COMBINING for a while, because a placing was held to leave nothing. The app
      // then invented a piece for it anyway — a number, a name, "(placed)" — so that the tree
      // had something to draw, and that piece was in no answer and in no numbering: the model
      // had never heard of it and went on naming the loose parts. One sequence, in gesture
      // order, and every piece in it is one the model can name.
      if (!sub.output?.en && !sub.output?.fr) continue;
      out.set(`${step.category}#${sub.letter}`, { id: String(n++), output: sub.output });
    }
  }
  return out;
}

// WHICH GESTURE EACH SUB-STEP IS, by what it does rather than where it stands: `<print>.<n>`,
// by key. What an answer about a sub-step is filed by when Phase 4 cuts a step again (see
// refile) and what a sign-off remembers it was given on (see gestureMoved).
//
// The key is a position — `<step>#<letter>` — and a re-run that splits one gesture into two moves
// every gesture after it. The print is what does not move: the kind, each part with its role,
// count and side, and the tools. NOT THE SENTENCE, which the model words afresh every run, and
// NOT THE PIECE'S NUMBER, which is a position too (101 is the step's first output): a piece is
// written as the print of the gesture that made it, so a chain of them is the same chain however
// it is numbered.
//
// `n` counts the gestures of the same step with the same print before this one: four washers
// slid onto four screws are one print, and the second of them is `.1` whatever the step around it
// became. A piece nobody can trace (a number no sub-step makes) is written as its number.
export function gestureIds(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
): Map<string, string> {
  const keyOfPiece = new Map([...pieceIdByKey(steps, subSteps)].map(([key, { id }]) => [id, key]));
  const prints = new Map<string, string>();
  const out = new Map<string, string>();
  for (const step of steps) {
    const seen = new Map<string, number>();
    for (const sub of subSteps[step.category] ?? []) {
      const key = `${step.category}#${sub.letter}`;
      const of = (id: string) => {
        if (Number(id) < MADE_ID_BASE) return `p${id}`;
        const made = keyOfPiece.get(id);
        return made && prints.has(made) ? `[${prints.get(made)}]` : `?${id}`;
      };
      const print = digest(JSON.stringify([
        sub.kind,
        sub.parts.map((p: UsedPart) => [of(p.id), p.role ?? '', p.qty ?? '', p.side ?? ''].join('/')).sort(),
        sub.tools.map(of).sort(),
      ]));
      prints.set(key, print);
      const n = seen.get(print) ?? 0;
      seen.set(print, n + 1);
      out.set(key, `${print}.${n}`);
    }
  }
  return out;
}

// A HARDWARE PREPARED IN THE HAND IS NOT A SUB-STEP OF ITS OWN: the washer slid onto the screw
// is the first half of putting that screw in, and is shown as such — in Phase 4's table, in
// Phase 6's strip and animation, and on the published page. By key: each preparation (a
// sub-step that makes hardware, see makesHardware) to the first later sub-step that uses what it
// made. A preparation used by another preparation (a nut onto a prepared screw) goes on to that
// one's user. One that nothing uses stays a sub-step.
//
// Phase 4's own answer is not changed: the preparation keeps its row, its letter and the piece
// it numbers (101, "prepared screw"), which every later sub-step, rule and bake reads.
export function foldsOf(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
): Map<string, string> {
  const pieces = pieceIdByKey(steps, subSteps);
  const order = steps.flatMap((step) => (subSteps[step.category] ?? [])
    .map((sub) => ({ key: `${step.category}#${sub.letter}`, sub })));
  const userOf = new Map<string, string>();
  order.forEach(({ key, sub }, i) => {
    const made = pieces.get(key)?.id;
    if (!made || !makesHardware(sub.kind)) return;
    const user = order.slice(i + 1).find((o) => o.sub.parts.some((p) => p.id === made));
    if (user) userOf.set(key, user.key);
  });
  const out = new Map<string, string>();
  for (const prep of userOf.keys()) {
    let user = userOf.get(prep)!;
    for (let hop = 0; userOf.has(user) && hop < order.length; hop++) user = userOf.get(user)!;
    out.set(prep, user);
  }
  return out;
}

// The print alone, without its place among its twins — what a sign-off is compared on: the second
// of four identical gestures answered as the first is answered right.
export const printOf = (id: string | null | undefined): string | null => (id ? id.split('.')[0] : null);

// EVERY PIECE THE MANUAL ASSEMBLES, BY ID, NAMED IN THE LANGUAGE ON SCREEN — what a sentence
// needs to render a {101} token, and what several readers would otherwise each build for
// themselves out of the same two maps. Off pieceIdByKey, so a name here belongs to the id
// that map hands out and not to one counted a second time.
//
// WITH ITS BASE PART, for the sentence: a reader is told "the side rail (C, Right)", not "First
// side rail with first barrel nut inserted (104)" — see pieceBases.
export type PieceName = { name: string; base?: PieceBase };
export function pieceNames(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  language: string,
): Map<string, PieceName> {
  const bases = pieceBases(steps, subSteps);
  const out = new Map<string, PieceName>();
  for (const { id, output } of pieceIdByKey(steps, subSteps).values()) {
    const name = subStepOutputName(output, language);
    if (name) out.set(id, { name, base: bases.get(id) });
  }
  return out;
}

// The sub-step that MADE each piece, by the piece's id — the reverse of the numbering.
function pieceMakers(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
): Map<string, SubStep> {
  const maker = new Map<string, SubStep>();
  const byKey = pieceIdByKey(steps, subSteps);
  for (const step of steps) {
    for (const sub of subSteps[step.category] ?? []) {
      const made = byKey.get(`${step.category}#${sub.letter}`);
      if (made) maker.set(made.id, sub);
    }
  }
  return maker;
}

// THE SUPPLIED PART A PIECE IS BUILT ON — the one that was there first and had the rest put
// onto it. A rail with a barrel nut in it is still, to somebody holding it, the rail.
//
// Walked down the RECEIVING end of each gesture: the piece's maker received one part, and if
// that was itself a piece, its maker received one, and so on to a part from the box. Its side
// comes with it, from the entry that names it. A maker of a single part (a placing) has that
// part as its base whatever its role says.
//
// NO BASE where the walk cannot name exactly one: no roles answered, or two parts received at
// once. The sentence then names the piece as it always did, rather than a part guessed at.
export type PieceBase = { id: string; side?: Side };
export function pieceBases(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
): Map<string, PieceBase> {
  const maker = pieceMakers(steps, subSteps);
  const out = new Map<string, PieceBase>();
  const walk = (id: string, seen: Set<string>): PieceBase | undefined => {
    const cached = out.get(id);
    if (cached) return cached;
    const sub = maker.get(id);
    if (!sub || seen.has(id)) return undefined;
    seen.add(id);
    const parts = sub.parts ?? [];
    const receiving = parts.filter((u) => u.role === 'receiving');
    const held = receiving.length ? receiving : parts.length === 1 ? parts : [];
    if (held.length !== 1) return undefined;
    const u = held[0];
    const base = Number(u.id) >= MADE_ID_BASE ? walk(u.id, seen) : { id: u.id, side: u.side };
    if (base) out.set(id, base);
    return base;
  };
  for (const id of maker.keys()) walk(id, new Set());
  return out;
}

export function piecesBefore(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
  idx: number,
): { id: string; name: string; base?: string; inside: string[] }[] {
  const all = pieceIdByKey(steps, subSteps);
  const bases = pieceBases(steps, subSteps);
  const contents = pieceContents(steps, subSteps);
  const out: { id: string; name: string; base?: string; inside: string[] }[] = [];
  for (let i = 0; i < idx && i < steps.length; i++) {
    for (const sub of subSteps[steps[i].category] ?? []) {
      const made = all.get(`${steps[i].category}#${sub.letter}`);
      // English, because this list is prompt copy. The reader's two languages are drawn from
      // the same field elsewhere.
      if (made) {
        out.push({
          id: made.id,
          name: made.output.en || made.output.fr,
          base: bases.get(made.id)?.id,
          inside: contents.get(made.id) ?? [],
        });
      }
    }
  }
  return out;
}

// WHICH SUPPLIED PARTS ARE INSIDE A PIECE — every one of them, however deep it was buried.
//
// A piece is what a gesture left, and the gesture after it works on THAT PIECE by id: "drive
// {103} home" names one violet object where the manual sees a rail bolted to a frame. Which is
// exactly right for the sentence and exactly wrong for anything that has to point at a plank —
// a hole is bored in a rail, not in a rail-and-frame, and Phase 5 models the rail.
//
// So this walks a piece back down to the parts that came in the box. `parts` on the sub-step
// that made it, expanded: an id below the piece range is a supplied part and is kept, an id in
// it is another piece and is expanded in turn.
//
// `seen` guards a cycle. A piece that swallowed itself is not a thing the prompt can produce,
// but a page that hung because a model wrote one would be a poor way to find out.
export function pieceContents(
  steps: StepBand[],
  subSteps: Record<string, SubStep[]>,
): Map<string, string[]> {
  const maker = pieceMakers(steps, subSteps);
  const out = new Map<string, string[]>();
  const walk = (id: string, seen: Set<string>): string[] => {
    const cached = out.get(id);
    if (cached) return cached;
    const sub = maker.get(id);
    if (!sub || seen.has(id)) return [];
    seen.add(id);
    const inside: string[] = [];
    for (const u of sub.parts ?? []) {
      // A PIECE OR A PART, told apart by the one threshold this app tells them apart by
      // everywhere: an index into the parts list, or a number in the hundreds.
      if (Number(u.id) >= MADE_ID_BASE) inside.push(...walk(u.id, seen));
      else if (!inside.includes(u.id)) inside.push(u.id);
    }
    // Deduped at the end rather than as it goes: a piece reached by two paths is one piece,
    // and a part listed twice would read as two planks.
    const uniq = [...new Set(inside)];
    out.set(id, uniq);
    return uniq;
  };
  for (const id of maker.keys()) walk(id, new Set());
  return out;
}

// THE FIRST NUMBER THIS STEP'S PIECES TAKE. Now a function of the step alone — 101, 201,
// 301 — where it used to be a count of everything made before it, which meant a step could
// not be told its numbering until every step before it had been analysed.
export const nextPieceNo = (
  steps: StepBand[],
  _subSteps: Record<string, SubStep[]>,
  idx: number,
): number => stepPieceBase(steps[idx]?.num ?? 1);

// Analyze every assembly step in ONE Gemini call per step (all its diagram bands at once),
// ONE STEP AT A TIME in printed order: which earlier steps it builds on, AND its breakdown
// into ordered sub-steps with a per-sub-step part / tool allocation. Results stream in step
// by step, keyed by the step's category.
//
// IN ORDER, AND WAITING. The steps are independent as far as their CONTEXT goes — what each
// adds, what is already assembled, what each earlier step achieved all come from Phase 2's
// repartition, which is complete before this phase begins — and they were asked four at a
// time on exactly that argument. One thing does run through them: the cycle numbering, which
// counts across the whole manual and therefore cannot be answered by a call that does not
// know what the calls before it opened. Four at a time cannot know; one after another does.
//
// `byStep` is Phase 2's repartition — what each step adds. It is an INPUT here, not an
// output: the prompt states this step's parts as given, and the sub-step allocation is
// made to partition exactly them.
//
// `resume` keeps the steps a halted run already analysed and asks only for the rest.
// A step's presence in `subSteps` is what marks it done — and a skipped step still hands its
// cycle numbers to the ones after it, since they are read from the stored answer.
//
// `only` narrows the run to ONE step, by index — what the phase's own button does, since
// it names the step on screen. Every other step keeps its answer untouched, and this one
// is re-asked even though it already has one: asking for the step you are looking at is
// the whole point of the click. No progress bar over a single call — the spinner is the
// whole of the state.
export async function extractSteps(
  docId: string,
  file: Blob,
  steps: StepBand[],
  items: PartItem[],
  byStep: ByStepParts,
  stepText: ByStepText,
  model: string,
  t: (k: string) => string,
  resume = false,
  only?: number,
) {
  const entry = store.getEntry(docId);
  if (entry.snapshot.busy) return;
  if (!model || steps.length === 0) {
    update(docId, { error: t('notice.cat2.noRun') });
    return;
  }
  // An index that names no step is not "run everything": it is a click on a page that
  // has no step to analyse, and the honest answer to it is nothing.
  const single = only != null;
  if (single && !steps[only]) return;

  const halted = haltGate(docId);
  // THE EXPERT RULES IN BEFORE THE FIRST PROMPT IS WRITTEN (see expertRulesStore).
  await loadExpertRules();
  const total = single ? 1 : steps.length;
  // WHAT THE DOCUMENT STILL HAS WHILE THIS RUN EMPTIES THE SCREEN. Captured before the
  // update below replaces the snapshot, and only for steps this run is actually about: a
  // category the repartition no longer has is an orphan, and carrying it would keep an
  // answer to a step that is gone.
  const known = new Set(steps.map((st) => st.category));
  const kept = <T,>(m: Record<string, T>): Record<string, T> =>
    Object.fromEntries(Object.entries(m).filter(([c]) => known.has(c)));
  const before = entry.snapshot;
  // ONE STOPWATCH FOR THE WHOLE RUN, started before the first step and read again after the
  // last: what the phase costs is the walk from end to end, and a per-step timer restarting
  // twelve times would answer a question nobody has while hiding the one everybody does.
  const startedAt = Date.now();
  // A resume — and a single-step run — keep every step already analysed. A fresh run of
  // the whole phase wipes them, so each step shows "running" or "waiting" from the first
  // instant rather than last run's answer.
  const prior = resume || single ? entry.snapshot : null;
  // THE TOOLS AND THE PRINTED TEXT ARE NOT WRITTEN HERE ANY MORE, and are not dropped
  // either: both are Phase 3's answers now (see stepPartsRun and stepInstructionsRun), and
  // these two maps are what a manual analysed before the move still holds. Carried through
  // every run and persisted untouched, so re-running the analysis does not erase the only
  // copy a document has — the readers prefer Phase 3's and fall back to these.
  const tools: ByTools = { ...(prior?.tools ?? store.get(docId).tools) };
  const instructions: ByInstr = { ...(prior?.instructions ?? store.get(docId).instructions) };
  const buildsOn: ByBuildsOn = { ...(prior?.buildsOn ?? {}) };
  const subSteps: BySubSteps = { ...(prior?.subSteps ?? {}) };
  // THE STEPS THIS RUN HAS NOT RE-READ, as they are persisted beside the ones it has: with their
  // pieces renamed as the steps before them are cut again (see refileStep).
  const held: BySubSteps = kept(before.subSteps);
  const reasoning: Record<string, string> = { ...(prior?.reasoning ?? {}) };
  const timing: Record<string, number> = { ...(prior?.timing ?? {}) };

  update(docId, {
    busy: true,
    running: [],
    error: null,
    tools,
    buildsOn,
    instructions,
    subSteps,
    reasoning,
    timing,
    progress: single ? null : { done: 0, total },
    startedAt,
    // The previous run's time goes the moment this one starts: it is the last run's answer,
    // and while a run is going the clock beside the button is this one's.
    elapsed: null,
  });
  // THE WIPE IS NOT PERSISTED, and this is where it used to be. A fresh run clears last
  // run's answers so every step shows "running" or "waiting" from the first instant rather
  // than last run's answer — which is a fact about THIS SCREEN, and writing it to the server
  // told every other reader of this document that phase 4 had never been run, for as long
  // as the run took. A second tab refreshed mid-run hydrated an emptied phase with no flag
  // to explain it: empty bar, no results, and a button offering to do the work already
  // being done.
  //
  // What is written instead is last run's answers with this run's over them, step by step
  // (see persistStep's call below) — true of the document at every moment of the run, since
  // a step already re-read has its new answer and one not reached yet still has the one it
  // had. A run that dies halfway now leaves the answers it never got to, rather than taking
  // them with it.

  // SAY THE RUN IS HAPPENING, for the readers that are not this one. Cleared in the
  // `finally` below, however the run ends, and beaten on while it lasts — a flag nobody
  // clears is a flag that means "running" forever (see phaseWorking).
  //
  // A SINGLE STEP SAYS NOTHING. It is one call, over in a moment, and the flag is one key
  // for the whole document: a click on step 3 here would clear the flag of a FULL run going
  // in another tab — which is exactly the pair of tabs this exists for. Nothing claimed,
  // nothing cleared.
  const atWork = (done: number) => {
    if (!single) setWorking(docId, { phase: STEPS_PHASE, done, total });
  };
  atWork(0);
  const beat = single
    ? 0
    : window.setInterval(() => atWork(store.get(docId).progress?.done ?? 0), BEAT_MS);
  // AND STOPPED FROM ANOTHER TAB, where one asks (see watchRemoteStop).
  const unwatch = single ? () => {} : watchRemoteStop(docId);

  let loadingTask: PDFDocumentLoadingTask | null = null;
  try {
    const buf = await file.arrayBuffer();
    loadingTask = loadPdf(buf);
    const doc: PDFDocumentProxy = await loadingTask.promise;
    let done = 0;
    // The progress bar counts the steps of a full run. A single-step run has none, so
    // this stays null and nothing is counted.
    const bump = () => (single ? null : { done: ++done, total });

    // One step's whole call. Its CONTEXT comes from Phase 2's repartition — complete before
    // this phase begins, so it does not depend on any other step's answer — and the one
    // thing it does take from the steps before is where their cycle numbering got to.
    const ask = async (idx: number) => {
      const step = steps[idx];
      const { assembled, previousSteps } = stepAssemblyInputs(steps, byStep, stepText, items, idx);
      const earlierNums = new Set(previousSteps.map((s) => s.step));
      const stepParts = byStep[step.category] ?? [];

      // Render all of this step's diagram bands and send them together in one call.
      const images: string[] = [];
      const pages: number[] = [];
      for (const page of step.pages) {
        if (halted()) break;
        const band = step.bands[page] ?? { top: 0, bottom: 1 };
        const image = await renderPdfPageBand(doc, page, band.top, band.bottom);
        if (image) {
          images.push(image);
          pages.push(page);
        }
      }
      // Rendering is local and free; stopping here is what keeps the call below from
      // ever being sent, which is the only saving a halt can actually make.
      if (halted()) return;
      if (!images.length) {
        update(docId, { progress: bump() });
        return;
      }

      const { data } = await axios.post<{
        buildsOn?: number[];
        subSteps?: SubStep[];
        reasoning?: string;
        // Seconds the whole round trip took, the server's own measure — retries and the
        // fallback to the paid key included. See step_analyze, which times it.
        elapsed?: number;
      }>(
        '/api/notice/step-analyze',
        {
          model,
          images,
          pages,
          step: step.num,
          // The WHOLE prompt, assembled on the client (instructions + context + JSON
          // format) and sent verbatim — the same string the badge shows, so what is shown
          // is byte-for-byte what is sent. The server uses it as-is.
          prompt: buildStepAnalyzePrompt(
            buildStepPromptContext(
              items,
              {
                parts: stepParts,
                text: stepText[step.category]?.en ?? '',
                // Phase 3's answer for this step, taken from the same map this run is about
                // to persist — which is where a re-run of Phase 3 in this same pass lands.
                // PHASE 3'S OWN ANSWER, from the store that holds it (stepPartsRun), with
                // the map below as the fallback for a manual analysed while this phase was
                // still the one asking. They are two stores with one exported name each,
                // both called getStepTools, and this one — `tools` — is the OLD one: nothing
                // writes it any more, so reading it alone is how the tools stopped reaching
                // the prompt at all.
                tools: partsStepTools(docId)[step.category] ?? tools[step.category] ?? [],
              },
              assembled,
              previousSteps,
              piecesBefore(steps, subSteps, idx),
            ),
            !assembled.length,
            nextPieceNo(steps, subSteps, idx),
          ),
        },
      );

      // Keep only ids that are actually tools in the parts list (guards a mislabel).
      // buildsOn: keep only numbers that name a step BEFORE this one. Every earlier step
      // is nameable now, analysed or not — the repartition knows them all.
      buildsOn[step.category] = (data.buildsOn || [])
        .filter((n) => earlierNums.has(n))
        .sort((a, b) => a - b);
      // THE IDS COME BACK ONE HIGHER THAN THEY ARE STORED, because that is how they were
      // offered — see shownId. Put back at the moment they land, so everything downstream
      // (and everything already on disk) goes on counting from zero. A piece an earlier
      // sub-step assembled is numbered from 101 and is left alone: storedId knows how many
      // parts the manual has, and anything past that is not one of them.
      subSteps[step.category] = (data.subSteps || []).map((sub) => ({
        ...sub,
        // The sentence names its parts by id in braces now (see subStepText), so its ids are
        // shifted with the rest of them — the tokens and the entries they draw from have to
        // be the same ids or the sentence renders a part the gesture does not list.
        textEn: storedTokens(sub.textEn, items.length),
        textFr: storedTokens(sub.textFr, items.length),
        ...(sub.beatsEn?.length ? { beatsEn: sub.beatsEn.map((b) => storedTokens(b, items.length)) } : {}),
        ...(sub.beatsFr?.length ? { beatsFr: sub.beatsFr.map((b) => storedTokens(b, items.length)) } : {}),
        parts: (sub.parts ?? []).map((u) => ({ ...u, id: storedId(u.id, items.length) })),
        tools: (sub.tools ?? []).map((id) => storedId(id, items.length)),
      }));
      reasoning[step.category] = data.reasoning || '';
      if (typeof data.elapsed === 'number') timing[step.category] = data.elapsed;

      // The five maps carry the run: this step's answer is written into them before the
      // next step is asked, which is also how that step finds the cycle numbers this one
      // opened.
      //
      // OVER WHAT WAS THERE, never instead of it. For a resume or a single step the two are
      // the same map and the merge is a no-op; for a fresh run it is the difference between
      // a half-finished run leaving a half-empty document and leaving one that still has
      // last run's answers under this run's.
      // EVERY ANSWER ABOUT THIS STEP'S SUB-STEPS GOES WHERE ITS GESTURE WENT — Phase 6's fittings
      // and pieces are filed by position, and this answer may have cut the step another way. See
      // refileStep. Measured against the document as the run found it: that is the cut they
      // were filed under.
      const { others, piecesFailed } = await refileStep(
        docId, steps, before.subSteps, { ...held, ...subSteps }, step.category,
      );
      for (const [c, subs] of Object.entries(others)) (c in subSteps ? subSteps : held)[c] = subs;
      if (piecesFailed) update(docId, { error: t('notice.steps.refileFailed') });
      persistStep(docId, {
        tools,
        instructions,
        buildsOn: { ...kept(before.buildsOn), ...buildsOn },
        subSteps: { ...held, ...subSteps },
        reasoning: { ...kept(before.reasoning), ...reasoning },
        timing: { ...kept(before.timing), ...timing },
      });
      update(docId, {
        tools: { ...tools },
        buildsOn: { ...buildsOn },
        instructions: { ...instructions },
        subSteps: { ...subSteps },
        reasoning: { ...reasoning },
        timing: { ...timing },
        progress: bump(),
      });
    };

    // In flight for as long as this step is being worked on — the band rendering included,
    // which is local and quick but is still this step. `finally`, so an early return or a
    // throw cannot leave a step spinning for the rest of the run.
    const runStep = async (idx: number) => {
      const { category } = steps[idx];
      markRunning(docId, category, true);
      try {
        await ask(idx);
      } finally {
        markRunning(docId, category, false);
      }
    };

    // The steps this run will actually ask about, decided up front and walked in order:
    // "skip this one, it is already answered" is a decision about the run, made once, rather
    // than a branch inside the loop that asks.
    const queue: number[] = [];
    for (let idx = 0; idx < steps.length; idx++) {
      if (single) {
        if (idx === only) queue.push(idx);
        continue;
      }
      // Already analysed, in the run being resumed: its call is paid for, so skip it and
      // only advance the progress bar. Never for a single-step run: that click asks for
      // THIS step, answer or no answer.
      if (steps[idx].category in subSteps) {
        update(docId, { progress: bump() });
        continue;
      }
      queue.push(idx);
    }

    for (const idx of queue) {
      if (halted()) break;
      try {
        await runStep(idx);
      } catch (e) {
        // One step's failure is one step's failure. It used to end the run — the loop threw
        // out to the catch below — so a single 502 on step 3 cost you steps 4 to 12 as
        // well, none of which had anything wrong with them. The message is shown, the step
        // keeps no answer, and re-running the phase (or that step) asks again. The steps
        // after it carry on, and their cycles continue from the last step that DID answer,
        // which is the only numbering available and leaves no gap.
        const msg =
          (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
          (e instanceof Error && e.message) ||
          t('notice.err.generic');
        // A step Stop cancelled is not a failure: it keeps no answer and says nothing.
        update(docId, isStopped(e) ? { progress: bump() } : { error: msg, progress: bump() });
      }
    }
  } catch (e) {
    const msg =
      (axios.isAxiosError(e) && (e.response?.data as { error?: string })?.error) ||
      (e instanceof Error && e.message) ||
      t('notice.err.generic');
    update(docId, { error: msg });
  } finally {
    if (beat) window.clearInterval(beat);
    unwatch();
    // NOTHING IS RUNNING HERE ANY MORE, said out loud rather than left to the beat's own
    // staleness: a reader would otherwise go on drawing this phase as busy for two minutes
    // after a run that has finished.
    if (!single) setWorking(docId, null);
    // Stopped wherever the run ended — finished, halted, or thrown out of. All three are a
    // run that took that long, and a stopwatch that only reports clean finishes is one you
    // cannot use to find out what the slow ones cost.
    update(docId, {
      busy: false,
      running: [],
      progress: null,
      startedAt: null,
      elapsed: Math.round((Date.now() - startedAt) / 1000),
    });
    void loadingTask?.destroy();
  }
}

export const useStepsRun = store.useSnapshot;

// The analysis as it stands, outside React — for the hardware table's call, chained after a run
// and reading what that run just wrote.
export const getSubSteps = (docId: string): Record<string, SubStep[]> => store.get(docId).subSteps;

// WHICH TOOLS EACH STEP CALLS FOR, by part index, outside React — the step analysis already
// answers it (a hand drawn holding a screwdriver, a tool printed in a step's header), and
// the drawing search needs it for the reason the repartition is not enough: the repartition
// says what a step ADDS, and a tool is used, not added. So a tool belonged to no step's
// question and was never looked for, in steps that draw it plainly.
export const getStepTools = (docId: string): Record<string, string[]> => store.get(docId).tools;
